import { IPackageOptions, IServiceCategory, IServiceRequestIds } from '../api/types';
import { IRecallByVin } from '../types/types';
import { EServiceCategoryType, ICategory } from '../store/reducers/categories/types';
import { TServiceCategory } from '../store/reducers/appointmentFrameReducer/types';

type TCategoryWithRequests = {
  id: number;
  type?: EServiceCategoryType;
  serviceRequests: { id: number }[];
};

/**
 * Service requests already saved on the appointment (received from the `by-key` request).
 * Their `categoryId` is the source of truth and must not be recalculated.
 */
type TExistingServiceRequest = {
  id: number;
  categoryId?: number | null;
};

/**
 * Only these category types own individually selected ops codes.
 * The same service request can also be listed inside a General category (such a category
 * is booked as a whole and is sent through `serviceCategories`), so it must never be used
 * as the `categoryId` of an ops code.
 */
const OPS_CODE_CATEGORY_TYPES = [
  EServiceCategoryType.IndividualServices,
  EServiceCategoryType.Diagnose,
];

const canOwnOpsCode = (category?: { type?: EServiceCategoryType } | null): boolean =>
  category?.type === undefined || OPS_CODE_CATEGORY_TYPES.includes(category.type);

/**
 * Returns full category data (with service requests) for categories selected during the flow.
 * Used to resolve which category a particular ops code belongs to.
 */
export const getSelectedCategoriesWithRequests = (
  allCategories: ICategory[],
  serviceCategories: TServiceCategory[]
): ICategory[] =>
  allCategories.filter(category => serviceCategories.some(item => item.id === category.id));

/**
 * Resolves the Open Recalls category id for the current flow,
 * so recall ops codes can be sent with their own `categoryId`.
 */
export const getRecallCategoryId = (
  s: IServiceCategory | null,
  sub: IServiceCategory | null,
  allCategories: ICategory[],
  serviceCategories: TServiceCategory[]
): number | undefined => {
  const isRecallCategory = (category?: { type?: EServiceCategoryType } | null) =>
    category?.type === EServiceCategoryType.OpenRecalls;

  // 1. Open Recalls category selected as a current (sub)service
  const currentRecallCategory = [sub, s].find(isRecallCategory);
  if (currentRecallCategory) return currentRecallCategory.id;

  // 2. Open Recalls category selected during the flow
  const selectedRecallCategory = getSelectedCategoriesWithRequests(
    allCategories,
    serviceCategories
  ).find(isRecallCategory);
  if (selectedRecallCategory) return selectedRecallCategory.id;

  // 3. Fallback: recalls can be selected without opening the Open Recalls card
  const recallCategories = allCategories.filter(isRecallCategory);
  const page = sub?.page ?? s?.page;

  return (
    (page !== undefined ? recallCategories.find(category => category.page === page) : undefined)
      ?.id ?? recallCategories[0]?.id
  );
};

const buildCategoryIdByRequestId = (
  s: IServiceCategory | null,
  sub: IServiceCategory | null,
  selectedRecalls?: IRecallByVin[],
  selectedCategories?: TCategoryWithRequests[],
  existingServiceRequests?: TExistingServiceRequest[],
  fallbackCategories?: TCategoryWithRequests[]
): Map<number, number> => {
  const categoryIdByRequestId = new Map<number, number>();

  const fillMissing = (categories?: TCategoryWithRequests[]) =>
    categories?.forEach(category => {
      if (!canOwnOpsCode(category)) return;
      category.serviceRequests?.forEach(serviceRequest => {
        if (!categoryIdByRequestId.has(serviceRequest.id)) {
          categoryIdByRequestId.set(serviceRequest.id, category.id);
        }
      });
    });

  // ops codes already saved on the appointment keep their own category,
  // so they are never re-assigned to the category card opened right now
  const existingRequestIds = new Set(existingServiceRequests?.map(item => item.id) ?? []);

  // 1. the category card opened right now (sub service wins over the service)
  //    is the most precise context for the ops codes added during this session
  [s, sub].forEach(category => {
    if (!canOwnOpsCode(category)) return;
    category?.serviceRequests?.forEach(serviceRequest => {
      if (!existingRequestIds.has(serviceRequest.id)) {
        categoryIdByRequestId.set(serviceRequest.id, category.id);
      }
    });
  });

  // 2. all categories selected during the flow (e.g. Individual Services + Diagnose)
  fillMissing(selectedCategories);

  // 3. last resort: any known category containing the ops code
  //    (needed in edit mode for codes restored from the appointment)
  fillMissing(fallbackCategories);

  // recall ops codes belong to the Open Recalls category even if it has no service requests
  if (selectedRecalls?.length) {
    const recallCategoryId =
      [sub, s].find(category => category?.type === EServiceCategoryType.OpenRecalls)?.id ??
      selectedCategories?.find(category => category.type === EServiceCategoryType.OpenRecalls)?.id;

    if (recallCategoryId !== undefined) {
      selectedRecalls.forEach(recall =>
        categoryIdByRequestId.set(recall.serviceRequestId, recallCategoryId)
      );
    }
  }

  // categories that came from the `by-key` appointment have the highest priority:
  // an already saved `categoryId` must be sent back as is,
  // unless it points to a category that cannot own an ops code (e.g. a General category)
  const notOwningCategoryIds = new Set(
    [...(selectedCategories ?? []), ...(fallbackCategories ?? [])]
      .filter(
        category => !canOwnOpsCode(category) && category.type !== EServiceCategoryType.OpenRecalls
      )
      .map(category => category.id)
  );

  existingServiceRequests?.forEach(serviceRequest => {
    const { categoryId } = serviceRequest;
    if (categoryId !== undefined && categoryId !== null && !notOwningCategoryIds.has(categoryId)) {
      categoryIdByRequestId.set(serviceRequest.id, categoryId);
    }
  });

  return categoryIdByRequestId;
};

export const collectServiceRequestIds = (
  s: IServiceCategory | null,
  sub: IServiceCategory | null,
  selectedPackage?: IPackageOptions | null,
  individualOpsCodes?: number[],
  selectedRecalls?: IRecallByVin[],
  individualOpsCodesComments?: Record<number, string>,
  selectedCategories?: TCategoryWithRequests[],
  existingServiceRequests?: TExistingServiceRequest[],
  fallbackCategories?: TCategoryWithRequests[]
): IServiceRequestIds[] => {
  const ids: number[] = [];

  if (selectedRecalls?.length) {
    selectedRecalls.forEach(item => ids.push(item.serviceRequestId));
  }
  if (individualOpsCodes?.length) {
    for (const c of individualOpsCodes) {
      ids.push(c);
    }
  }

  const set = new Set<number>(ids);
  const categoryIdByRequestId = buildCategoryIdByRequestId(
    s,
    sub,
    selectedRecalls,
    selectedCategories,
    existingServiceRequests,
    fallbackCategories
  );

  return Array.from(set).map(i => {
    const currComment = individualOpsCodesComments ? individualOpsCodesComments[i] : null;
    const categoryId = categoryIdByRequestId.get(i);
    return {
      id: i,
      comment: currComment ?? '',
      ...(categoryId !== undefined ? { categoryId } : {}),
    };
  });
};
